import { IMessageHandler } from "../message-handler";
import { IHandlerContext } from "../handler-context";
import { StorageMessage, StorageResponse, CryptoError } from "../../crypto/crypto-types";

interface LoginRequest {
  username: string;
  password: string;
}

interface LoginResponse {
  userId: string;
  username: string;
}

export class LoginHandler implements IMessageHandler<LoginRequest> {
  constructor(private context: IHandlerContext) {}

  canHandle(type: string): boolean {
    return type === "LOGIN";
  }

  async handle(message: StorageMessage<LoginRequest>, source: Window, origin: string): Promise<void> {
    const { username, password } = message.data;

    if (!username || !password) {
      this.context.sendError(source, 'Username and password required', 'MISSING_CREDENTIALS', origin, message.requestId);
      return;
    }

    // Check rate limiting
    const rateCheck = this.context.rateLimitService.check(username);
    if (!rateCheck.allowed) {
      this.context.sendError(
        source, 
        `Too many attempts. Try again in ${Math.ceil(rateCheck.delay / 1000)} seconds`,
        'RATE_LIMITED',
        origin,
        message.requestId
      );
      return;
    }

    try {
      // Get user profile
      const userProfile = await this.context.dbService.getUserByUsername(username);
      if (!userProfile) {
        this.context.rateLimitService.record(username);
        this.context.sendError(source, 'Invalid credentials', 'AUTH_ERROR', origin, message.requestId);
        return;
      }

      // Authenticate user
      const sessionKey = await this.context.cryptoEngine.authenticateUser(userProfile, password);
      
      // Update login information
      await this.context.dbService.updateUserLogin(userProfile.id);
      
      // Clear rate limiting on successful login
      this.context.rateLimitService.clear(username);
      const response: StorageResponse<LoginResponse> = {
        type: 'RESPONSE',
        data: {
          userId: userProfile.id,
          username: userProfile.username
        },
        requestId: message.requestId
      };
      this.context.sendResponse(source, response, origin, message.requestId);

      // Get portfolio data
      const portfolioData = await this.context.dbService.getPortfolio(userProfile.id);

      this.context.sendResponse(source, {
        success: true,
        user: {
          id: userProfile.id,
          username: userProfile.username,
          createdAt: userProfile.createdAt,
          lastLogin: userProfile.lastLogin,
          loginCount: userProfile.loginCount
        },
        portfolioData: portfolioData ? await this.context.cryptoEngine.decryptPortfolioData(portfolioData, sessionKey) : null,
        sessionData: {
          // We can't send the CryptoKey directly, so we'll handle session management differently
          userId: userProfile.id,
          loginTime: Date.now()
        }
      }, origin, message.requestId);

    } catch (error) {
      this.context.rateLimitService.record(username);
      if (error instanceof CryptoError && error.code === 'AUTH_ERROR') {
        this.context.sendError(source, 'Invalid credentials', 'AUTH_ERROR', origin, message.requestId);
      } else {
        throw error;
      }
    }
  }
}
